import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Ingredient Spotlight — fast, colourful, editorial ingredient discovery
// for exactly three official Riversol ingredients. Names, descriptions,
// imagery and Learn-More destinations are the exact live homepage banner
// content from riversol.com. This is intentionally shallow (ingredient →
// concise benefit → Learn More) so it does not repeat the deep Beta-T
// science section. Hinokitiol is treated at the same level as the others.
const INGREDIENTS = [
  {
    name: "Hinokitiol",
    description:
      "Our signature antioxidant, found in the Pacific red cedar, visibly brightens dark spots, improves skin clarity and evens skin texture.",
    href: "https://www.riversol.com/blogs/riversol/the-natural-benefits-of-beta-thujaplicin",
    image: "https://www.riversol.com/cdn/shop/files/hinokitiol.png?v=1772154769&width=1400",
    scrim: "linear-gradient(to top, rgba(9,38,29,0.92) 0%, rgba(9,38,29,0.35) 45%, rgba(9,38,29,0.05) 100%)",
    accent: "#8fd6b4",
  },
  {
    name: "Vitamin C",
    description:
      "Riversol's photostabilized Vitamin C delivers antioxidant protection while brightening skin and reducing dark spots. It's 10x more stable than other traditional forms of Vitamin C.",
    href: "https://www.riversol.com/pages/vitamin-c-and-skin-health",
    image: "https://www.riversol.com/cdn/shop/files/vitamin_c_9cbe2d53-d1c5-44f4-9939-f6b748070640.png?v=1772845187&width=1400",
    scrim: "linear-gradient(to top, rgba(120,52,14,0.92) 0%, rgba(120,52,14,0.35) 45%, rgba(120,52,14,0.05) 100%)",
    accent: "#ffcf99",
  },
  {
    name: "Retinol",
    description:
      "Retinol visibly improves the look of dark spots, fine lines and wrinkles. At Riversol, every retinol treatment is precision-formulated for sensitive skin to support both visible results and skin comfort.",
    href: "https://www.riversol.com/blogs/riversol/the-dos-and-don-ts-of-using-retinol-safely-in-the-summertime",
    image: "https://www.riversol.com/cdn/shop/files/retinol_1_c0071053-6d85-4253-bdb0-51ff50f4bfec.jpg?v=1773689761&width=1400",
    scrim: "linear-gradient(to top, rgba(14,33,64,0.92) 0%, rgba(14,33,64,0.35) 45%, rgba(14,33,64,0.05) 100%)",
    accent: "#a9c8f2",
  },
];

const Panel = ({ item, active }) => (
  <div className="relative shrink-0 grow-0 basis-[90%] sm:basis-[76%] lg:basis-[64%] xl:basis-[60%] pl-4 lg:pl-6">
    <div
      className={`relative overflow-hidden rounded-2xl bg-neutral-900 transition-[transform,filter] duration-700 ease-out ${
        active ? "brightness-100 scale-100" : "brightness-[0.4] scale-[0.97]"
      }`}
      style={{ height: "min(64vh, 620px)" }}
    >
      <motion.img
        src={item.image}
        alt={item.name}
        className="absolute inset-0 w-full h-full object-cover"
        animate={{ scale: active ? 1 : 1.05 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        draggable={false}
      />
      {/* Same linear shadow as "Shop by concern" — improves text legibility
          on every banner (including the fully-visible active one). */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-black/5 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 p-7 lg:p-10">
        <motion.div
          initial={false}
          animate={{ opacity: active ? 1 : 0.6, y: active ? 0 : 12 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-[10px] tracking-[0.28em] uppercase" style={{ color: item.accent }}>
            Ingredient
          </span>
          <h3 className="mt-3 text-white text-[40px] lg:text-[56px] leading-[0.98] tracking-[-0.02em]">
            {item.name}
          </h3>
          <p className="mt-4 text-white/85 text-[14px] lg:text-[15px] leading-relaxed max-w-[460px]">
            {item.description}
          </p>
          <a
            href={item.href}
            data-testid={`ingredient-cta-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            className="mt-7 inline-flex items-center gap-2 text-white text-[12px] tracking-[0.16em] uppercase font-medium border-b border-white/40 pb-2 hover:border-white transition-colors"
          >
            Learn more
            <ArrowRight size={15} strokeWidth={1.8} />
          </a>
        </motion.div>
      </div>
    </div>
  </div>
);

export const IngredientSpotlightClassic = () => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: false, align: "start", containScroll: "trimSnaps" });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelected(embla.selectedScrollSnap());
    setCanPrev(embla.canScrollPrev());
    setCanNext(embla.canScrollNext());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    return () => {
      embla.off("select", onSelect);
      embla.off("reInit", onSelect);
    };
  }, [embla, onSelect]);

  return (
    <section className="bg-riversol-paper py-24 lg:py-32" data-testid="home-ingredient-spotlight">
      <div className="max-w-[1600px] mx-auto">
        <div className="px-6 lg:px-10 flex items-end justify-between gap-8 mb-10 lg:mb-14">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="hidden font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500"
            >
              Ingredient spotlight
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-4 text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.05] tracking-[-0.02em] text-neutral-900 max-w-[640px]"
            >
              The actives behind{" "}
              <span className="italic text-riversol-blue">the results.</span>
            </motion.h2>
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => embla && embla.scrollPrev()}
              disabled={!canPrev}
              aria-label="Previous ingredient"
              data-testid="ingredient-prev"
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${
                canPrev ? "border-neutral-400 text-neutral-900 hover:border-riversol-blue hover:text-riversol-blue" : "border-neutral-200 text-neutral-300 cursor-not-allowed"
              }`}
            >
              <ArrowLeft size={18} strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={() => embla && embla.scrollNext()}
              disabled={!canNext}
              aria-label="Next ingredient"
              data-testid="ingredient-next"
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${
                canNext ? "border-neutral-400 text-neutral-900 hover:border-riversol-blue hover:text-riversol-blue" : "border-neutral-200 text-neutral-300 cursor-not-allowed"
              }`}
            >
              <ArrowRight size={18} strokeWidth={1.6} />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden cursor-grab active:cursor-grabbing pl-6 lg:pl-10 pr-2" ref={emblaRef}>
          <div className="flex">
            {INGREDIENTS.map((item, idx) => (
              <Panel key={item.name} item={item} active={idx === selected} />
            ))}
          </div>
        </div>

        {/* Progress */}
        <div className="px-6 lg:px-10 mt-8 flex items-center gap-3">
          {INGREDIENTS.map((item, idx) => (
            <button
              key={item.name}
              type="button"
              onClick={() => embla && embla.scrollTo(idx)}
              aria-label={`Go to ${item.name}`}
              data-testid={`ingredient-dot-${idx}`}
              className="py-2"
            >
              <span
                className={`block h-[2px] transition-all duration-500 ${
                  idx === selected ? "w-10 bg-riversol-blue" : "w-5 bg-neutral-300"
                }`}
              />
            </button>
          ))}
          <span className="ml-2 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400 tabular-nums">
            {String(selected + 1).padStart(2, "0")} / {String(INGREDIENTS.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
};

export default IngredientSpotlightClassic;
